// AppointmentPDF.tsx - Enhanced UI with Better Alignment
import React from "react";

import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import logo from "../images/logoNew.png";

interface Medicine {
  itemName: string;
  doseText: string;
  doseValue: number;
  forDays: number;
}


interface LabItem {
  serviceName: string;
}


interface AppointmentDetail {
  CheifComplain: string;
  ProvisionalDiagnosis: string;
  TreatmentPlan: string;
  Medicines: Medicine[];
  LabItems: LabItem[];
}


interface DoctorInfo {
  doctorName: string;
  specializationName: string;
  appointmentDate: string;
  slotStartTime: string;
  // location:string
}


interface PatientInfo {
  patientName: string;
  dob: string;
  gender: string;
  address: string;
  phoneNo: string;
}


interface Props {
  appointmentId: string;
  doctorInfo: DoctorInfo;
  patientInfo: PatientInfo;
  appointmentDetail: AppointmentDetail;
  followUpDate:string
}


// ---------- Helpers for Indian Date/Time ----------
const formatDate = (dateStr: string) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
      year: "numeric",
    timeZone: "Asia/Kolkata",
  });
};


const formatTime = (dateStr: string, timeStr: string) => {
  if (!dateStr || !timeStr) return "";
  const [hour, minute] = timeStr.split(":");
  const date = new Date(dateStr);
  date.setHours(parseInt(hour), parseInt(minute));
  return date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  });
};


const getDayName = (dateStr: string) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    weekday: "long",
    timeZone: "Asia/Kolkata",
  });
};


const styles = StyleSheet.create({
  page: {
    fontSize: 8,
    lineHeight: 1.5,
    paddingTop: 20,
    paddingBottom: 80,
    paddingHorizontal: 35,
    fontFamily: "Helvetica",
  },


  // Enhanced Header with Better Alignment
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 2,
    borderColor: "#1e3a8a",
    paddingBottom: 10,
    marginBottom: 12,
    minHeight: 60,
  },


  headerColLeft: {
    flex: 1,
    alignItems: "flex-start",
    justifyContent: "center",
    paddingRight: 15,
  },


  headerColCenter: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },


  headerColRight: {
    flex: 1,
    alignItems: "flex-end",
    justifyContent: "center",
    paddingLeft: 15,
  },


  // Doctor Information - Better Alignment
  doctorName: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#1e3a8a",
    marginBottom: 3,
    textAlign: "left",
  },

  doctorSpec: {
    fontSize: 9,
    color: "#555",
    marginBottom: 2,
    textAlign: "left",
  },

  doctorContact: {
    fontSize: 9,
    color: "#666",
    textAlign: "left",
  },


  // Logo and Clinic - Centered Alignment
  logo: {
    height: 32,
    marginBottom: 4,
  },


  clinicName: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#1e3a8a",
    textAlign: "center",
    marginBottom: 1,
  },
  

  clinicSubtext: {
    fontSize: 8,
    color: "#444",
    textAlign: "center",
    marginBottom: 1,
  },

  emergency: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#dc2626",
    textAlign: "center",
    marginBottom: 1,
  },

  website: {
    fontSize: 8,
    color: "#2563eb",
    textDecoration: "underline",
    textAlign: "center",
  },

  // Right Column - Right Aligned
  rightText: {
    fontSize: 8,
    color: "#444",
    textAlign: "right",
    marginBottom: 1,
  },

  // Enhanced Info Grid with Better Spacing
  infoGrid: {
    marginTop: 1,
    marginBottom: 8,
    flexDirection: "row",
    flexWrap:"wrap",
    borderBottomWidth: 0.8,
    borderBottomColor: "#e5e7eb",
    paddingBottom: 6,
  },

  infoItem: {
    width: "50%",
    fontSize: 8,
    marginBottom: 2,
    paddingHorizontal: 4,
    flexDirection: "row",
    alignItems: "baseline",
  },

  label: {
    fontSize: 8,
    fontWeight: "bold",
    color: "#374151",
    width: "42%",
    textAlign: "left",
  },

  infoValue: {
    fontSize: 8,
    color: "#374151",
    width: "58%",
    textAlign: "left",
  },

  // Enhanced Section Styling
  section: {
    marginBottom: 8,
  },

  title: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#009A85",
    marginBottom: 1,
    paddingBottom: 0,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },

  // Better List Item Alignment
  listItem: {
    fontSize: 8,
    marginLeft: 2,
    marginBottom: 2,
    lineHeight: 1.4,
    color: "#374151",
  },

  italicText: {
    fontSize: 8,
    fontStyle: "italic",
    color: "#6b7280",
    marginLeft: 4,
    marginTop: 4,
  },

  // Enhanced Medicine Header
  medHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
    paddingBottom: 2,
  },
  
  medicineTitle: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#009A85",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },

  linkText: {
    fontSize: 7,
    backgroundColor: "#009A85",
    color: "#ffffff",
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 3,
    textTransform: "uppercase",
    fontWeight: "bold",
    textAlign: "center",
  },

tableHeader: {
  flexDirection: "row",
  borderTopWidth: 1,
  borderBottomWidth: 1,
  borderColor: "#374151",
  backgroundColor: "#f0f4f8",
  paddingVertical: 4,
  marginBottom: 1,
  alignItems: "center", 
},

tableHeaderCell: {
  fontSize: 8,
  fontWeight: "bold",
  color: "#1f2937",
  paddingHorizontal: 4,
  textAlign: "left",
},

tableRow: {
  flexDirection: "row",
  borderBottomWidth: 0.5,
  borderColor: "#d1d5db",
  paddingVertical: 1,
  minHeight: 24,
  alignItems: "center",
},

tableCellCenter: {
  fontSize: 8,
  paddingHorizontal: 4,
  color: "#374151",
  textAlign: "center",
},

// Medicine Name Styling (unchanged)
medicineNameCell: {
  fontSize: 8,
  fontWeight: "bold",
  color: "#1f2937",
  paddingHorizontal: 4,
  textAlign: "left",
},

// Dose Column with Better Alignment
doseColumn: {
  flexDirection: "column",
  alignItems: "flex-start", // ← Changed from "flex-start" to "center"
  justifyContent: "flex-start", // ← Changed from "flex-start" to "center"
  paddingHorizontal: 2,
},

doseText: {
  fontSize: 8,
  color: "#374151",
  textAlign: "center",
},

doseSubtext: {
  fontSize: 7,
  color: "#6b7280",
  textAlign: "center",
},


  // Enhanced Notes Section
  notesRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 6,
    paddingTop: 4,
    borderTopWidth: 0.5,
    borderTopColor: "#e5e7eb",
  },

  notesText: {
    fontSize: 7,
    color: "#6b7280",
    lineHeight: 1.3,
  },

  notesLeft: {
    flex: 1,
    paddingRight: 8,
  },

  notesRight: {
    flex: 1,
    paddingLeft: 8,
  },

  // Enhanced Follow-up Section
  followUpSection: {
    marginTop: 6,
  },

  followUpTitle: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#009A85",
    marginBottom: 3,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },

  followUpText: {
    fontSize: 8,
    color: "#374151",
    marginLeft: 4,
  },

  // Enhanced Disclaimer
  disclaimer: {
    fontSize: 8,
    color: "#6b7280",
    lineHeight: 1.4,
    marginTop: 8,
    padding: 8,
    backgroundColor: "#f8fafc",
    borderRadius: 4,
    borderLeftWidth: 3,
    borderLeftColor: "#009A85",
  },

  disclaimerLabel: {
    fontWeight: "bold",
    color: "#009A85",
  },

  // Enhanced Signature Block
  signatureBlock: {
    marginTop: 1,
    alignItems: "flex-end",
    paddingRight: 20,
  },

  signatureArea: {
    alignItems: "center",
    width: 140,
  },

  signatureText: {
    fontSize: 8,
    fontStyle: "italic",
    color: "#6b7280",
    marginBottom: 16,
    textAlign: "center",
  },

  signatureLine: {
    width: 120,
    borderBottomWidth: 1,
    borderBottomColor: "#374151",
    marginBottom: 4,
  },

  signatureName: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#1f2937",
    textAlign: "center",
  },

  signatureSpec: {
    fontSize: 8,
    color: "#6b7280",
    textAlign: "center",
    marginTop: 2,
  },

  // Enhanced Footer with Better Alignment
  footer: {
    position: "absolute",
    bottom: 20,
    left: 35,
    right: 35,
    borderTopWidth: 2,
    borderColor: "#1e3a8a",
    paddingTop: 10,
    flexDirection: "row",
    justifyContent: "space-between",
  },

  footerColumn: {
    width: "48%",
  },

  footerHeading: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#1e3a8a",
    textTransform: "uppercase",
    marginBottom: 4,
    letterSpacing: 0.5,
  },

  footerText: {
    fontSize: 7,
    color: "#4b5563",
    marginBottom: 2,
    lineHeight: 1.3,
  },

  // Page Number
  pageNumber: {
    position: "absolute",
    bottom: 10,
    left: 0,
    right: 0,
    textAlign: "center",
    fontSize: 7,
    color: "#6b7280",
  },
  qrCodeContainer: {
  alignItems: "flex-start",
  marginTop: 12,
  marginBottom: 2,
},
qrCodeLabel: {
  fontSize: 8,
  color: "#374151",
  textAlign: "center",
  marginTop: 4,
},
});
const getQrUrl = (doctorName: string) => {
  // const url = `https://oas.ramahospital.com/doctor/noida/general-medicine/${doctorName
  //   .replace(/\s+/g, "-")
  //   .toLowerCase()}`;
  const url = `https://oas.ramahospital.com/doctors`;
  return `https://quickchart.io/qr?text=${encodeURIComponent(url)}&size=100`;
};
const AppointmentPDF: React.FC<Props> = ({
  appointmentId,
  doctorInfo,
  patientInfo,
  appointmentDetail,
  followUpDate
}) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.headerRow}>
        
        <View style={styles.headerColLeft}>
          <Text style={styles.doctorName}>{doctorInfo.doctorName}</Text>
          <Text style={styles.doctorSpec}>{doctorInfo.specializationName}</Text>
          {/* <Text style={styles.doctorContact}>Mob: +91 89833 90126</Text> */}
        </View>
        
        <View style={styles.headerColCenter}>
          <Image src={logo} style={styles.logo} />
        </View>
        
        <View style={styles.headerColRight}>
             <Text style={styles.clinicName}>Rama Hospital</Text>
          <Text style={styles.rightText}>Multi-Super Speciality Hospital</Text>
          <Text style={styles.rightText}>E-2, Sector-61, Noida, UP - 201301</Text>
          <Text style={styles.website}>www.ramahospital.com</Text>
        </View>
      </View>

      
      <View style={styles.infoGrid}>
        <View style={styles.infoItem}>
          <Text style={styles.label}>Appointment ID:</Text>
          <Text style={styles.infoValue}>{appointmentId}</Text>
        </View>

        <View style={styles.infoItem}>
          <Text style={styles.label}>Date & Time:</Text>
          <Text style={styles.infoValue}>
            {formatDate(doctorInfo.appointmentDate)} - {formatTime(doctorInfo.appointmentDate,doctorInfo.slotStartTime)}
          </Text>
        </View>

        <View style={styles.infoItem}>
          <Text style={styles.label}>Patient Name:</Text>
          <Text style={styles.infoValue}>{patientInfo.patientName}</Text>
        </View>

        <View style={styles.infoItem}>
          <Text style={styles.label}>Gender:</Text>
          <Text style={styles.infoValue}>{patientInfo.gender}</Text>
        </View>

        <View style={styles.infoItem}>
          <Text style={styles.label}>Mobile:</Text>
          <Text style={styles.infoValue}>{patientInfo.phoneNo}</Text>
        </View>

        <View style={styles.infoItem}>
          <Text style={styles.label}>Doctor Name:</Text>
          <Text style={styles.infoValue}>{doctorInfo?.doctorName}</Text>
        </View>

        <View style={styles.infoItem}>
          <Text style={styles.label}>Specialization:</Text>
          <Text style={styles.infoValue}>{doctorInfo?.specializationName}</Text>
        </View>

        <View style={styles.infoItem}>
          <Text style={styles.label}>Consult Type:</Text>
          <Text style={styles.infoValue}>Video Consultation</Text>
        </View>
      </View>

      {/* Chief Complaints */}
      <View style={styles.section}>
        <Text style={styles.title}>Chief Complaints</Text>
        {appointmentDetail.CheifComplain ? (
          appointmentDetail.CheifComplain.split(",").map((item, idx) => (
            <Text key={idx} style={styles.listItem}>
              • {item.trim()}
            </Text>
          ))
        ) : (
          <Text style={styles.italicText}>None provided</Text>
        )}
      </View>

      {/* Diagnosis */}
      <View style={styles.section}>
        <Text style={styles.title}>Diagnosis</Text>
        {appointmentDetail.ProvisionalDiagnosis ? (
          appointmentDetail.ProvisionalDiagnosis.split(",").map((item, idx) => (
            <Text key={idx} style={styles.listItem}>
              • {item.trim()}
            </Text>
          ))
        ) : (
          <Text style={styles.italicText}>No diagnosis provided</Text>
        )}
      </View>

      {/* Treatment Plan */}     
      <View style={styles.section}>
        <Text style={styles.title}>Treatment Plan</Text>
        {appointmentDetail.TreatmentPlan ? (
          appointmentDetail.TreatmentPlan.split(",").map((item, idx) => (
            <Text key={idx} style={styles.listItem}>
              • {item.trim()}
            </Text>
          ))
        ) : (
          <Text style={styles.italicText}>No treatment plan provided</Text>
        )}
      </View>

      {/* Investigations */}
      <View style={styles.section}>
        <Text style={styles.title}>Investigations</Text>
        {appointmentDetail.LabItems && appointmentDetail.LabItems.length > 0 ? (
          appointmentDetail.LabItems.map((lab, idx) => (
            <Text key={idx} style={styles.listItem}>
              • {lab.serviceName}
            </Text>
          ))
        ) : (
          <Text style={styles.italicText}>No investigations provided</Text>
        )}
      </View>

      {/* Enhanced Medication Section */}
      <View style={styles.section}>
  {/* Header with perfect alignment */}
  <View style={styles.medHeader}>
    <Text style={styles.title}>Medication Prescribed</Text>
    <Text style={styles.linkText}>Click To Order Medicine</Text>
  </View>

{/* Enhanced Table with Perfect Column Alignment */}
<View style={styles.tableHeader}>
  <Text style={[styles.tableHeaderCell, { flex: 3 }]}>Medicine Name</Text>
  <Text style={[styles.tableHeaderCell, { flex: 1.5, textAlign: "center" }]}>Dosage</Text>
  <Text style={[styles.tableHeaderCell, { flex: 2, textAlign: "center" }]}>Medicine Details</Text>
  <Text style={[styles.tableHeaderCell, { flex: 1, textAlign: "center" }]}>Duration</Text>
</View>

{/* Table body with consistent alignment */}
{appointmentDetail.Medicines && appointmentDetail.Medicines.length > 0 ? (
  appointmentDetail.Medicines.map((m, idx) => (
    <View key={idx} style={styles.tableRow}>
      <Text style={[styles.medicineNameCell, { flex: 3 }]}>
        {idx + 1}. {m.itemName}
      </Text>
      <View style={[styles.doseColumn, { flex: 1 }]}>
        <Text style={styles.doseText}>{m.doseText}</Text>
        <Text style={styles.doseSubtext}>(M-N-E-N)</Text>
      </View>
      <Text style={[styles.tableCellCenter, { flex: 2 }]}>{m.doseValue}</Text>
      <Text style={[styles.tableCellCenter, { flex: 1 }]}>{m.forDays} days</Text>
    </View>
  ))
) : (
  <Text style={styles.italicText}>No medicines prescribed</Text>
)}


  {/* Enhanced Notes with Better Layout */}
  <View style={styles.notesRow}>
    <View style={styles.notesLeft}>
      <Text style={styles.notesText}>
        <Text style={{ fontWeight: "bold" }}>M-N-E-N:</Text> Morning - Noon - Evening - Night
      </Text>
    </View>
    <View style={styles.notesRight}>
      <Text style={styles.notesText}>
        <Text style={{ fontWeight: "bold" }}>NOTE:</Text> Medicine substitution allowed wherever applicable
      </Text>
    </View>
  </View>
</View>

      {/* Enhanced Follow up Section */}
      <View style={styles.followUpSection}>
        <Text style={styles.title}>Follow up Date</Text>
        <Text style={styles.followUpText}>
          {formatDate(followUpDate)} ({getDayName(followUpDate)})
        </Text>
      </View>

      {/* Enhanced Disclaimer */}
      <View style={styles.disclaimer}>
        <Text>
          <Text style={styles.disclaimerLabel}>Disclaimer: </Text>
          This prescription is issued on the basis of your inputs during Video Consultation. 
          It is valid from the date of issue until the specific period/dosage of each medicine as advised.
        </Text>
      </View>
 <View style={styles.qrCodeContainer}>
        <Image src={getQrUrl(doctorInfo.doctorName)} style={{ width: 60, height: 60 }} />
        <Text style={styles.qrCodeLabel}>
          Scan to Book the Follow Up
        </Text>
      </View>
      {/* Enhanced Signature Block */}
      <View style={styles.signatureBlock}>
        <View style={styles.signatureArea}>
          <Text style={styles.signatureText}>Signature</Text>
          <View style={styles.signatureLine} />
          <Text style={styles.signatureName}>{doctorInfo.doctorName}</Text>
          <Text style={styles.signatureSpec}>{doctorInfo.specializationName}</Text>
        </View>
      </View>

      {/* Enhanced Footer */}
      <View style={styles.footer}>
        {/* Left Column */}
        <View style={styles.footerColumn}>
          <Text style={styles.footerHeading}>Rama Hospital</Text>
          <Text style={styles.footerText}>
            E-2, Sector-61, Noida, Uttar Pradesh - 201301
          </Text>
          <Text style={styles.footerText}>
            info@ramahospital.com | www.ramahospital.com | +91 9717889898
          </Text>
        </View>

        {/* Right Column */}
        <View style={styles.footerColumn}>
          <Text style={styles.footerHeading}>Registered Office</Text>
          <Text style={styles.footerText}>
            A Unit of SJPI Medicare Pvt Ltd (Formerly known as Yuvaan Medicare Pvt Ltd)
          </Text>
          <Text style={styles.footerText}>
            Plot No. C-20 / 1A / 6, 1st A, Sector-62, Noida, Uttar Pradesh - 201309
          </Text>
          <Text style={styles.footerText}>
            info@sjpi.in | www.sjpi.in | CIN - U85200UP2018PTC108346
          </Text>
        </View>
      </View>

      <Text
        style={styles.pageNumber}
        render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
      />
    </Page>
  </Document>
);

export default AppointmentPDF;